import type { ReactNode } from 'react';
import { observer } from 'mobx-react';
import { Typography } from 'antd';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';

const { Text } = Typography;

interface BrandedEmptyProps {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

// Replacement for the default antd Empty. Branded, on-palette, with proper
// hierarchy and an optional CTA. Use everywhere the user might land on a
// blank state — every blank page is an opportunity to teach the product.

export const BrandedEmpty = observer(function BrandedEmpty({
  icon,
  title,
  description,
  action,
  size = 'md',
}: BrandedEmptyProps) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  const padding = size === 'lg' ? 64 : size === 'sm' ? 24 : 40;
  const iconSize = size === 'lg' ? 56 : size === 'sm' ? 36 : 44;

  return (
    <div
      style={{
        textAlign: 'center',
        padding,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
      }}
    >
      {icon && (
        <div
          style={{
            width: iconSize + 16,
            height: iconSize + 16,
            borderRadius: 999,
            background: isDark
              ? `linear-gradient(135deg, ${colors.ink[700]}40, ${colors.violet[700]}40)`
              : `linear-gradient(135deg, ${colors.ink[50]}, ${colors.violet[50]})`,
            color: colors.ink[600],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: iconSize / 2,
          }}
        >
          {icon}
        </div>
      )}
      <div>
        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: isDark ? colors.slate[100] : colors.slate[900],
            marginBottom: 4,
          }}
        >
          {title}
        </div>
        {description && (
          <Text
            type="secondary"
            style={{ fontSize: 13, maxWidth: 360, display: 'inline-block', lineHeight: 1.5 }}
          >
            {description}
          </Text>
        )}
      </div>
      {action && <div style={{ marginTop: 4 }}>{action}</div>}
    </div>
  );
});
